import { css, useTheme } from '@emotion/react';
import { forwardRef } from 'react';

import { fontSizes, glass, radii, shadows, spacing } from '@/theme/tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonShape = 'pill' | 'rect';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  shape?: ButtonShape;
  size?: ButtonSize;
}

const sizeStyles = {
  sm: { padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSizes.sm },
  md: { padding: `10px ${spacing.lg}`, fontSize: fontSizes.md },
  lg: { padding: `12px ${spacing.xl}`, fontSize: fontSizes.md },
} as const;

const shapeRadius = {
  pill: radii.pill,
  rect: radii.button,
} as const;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', shape = 'pill', size = 'md', className, ...props },
  ref,
) {
  const theme = useTheme();
  const s = sizeStyles[size];

  const variantStyles = {
    primary: css`
      background: ${theme.colors.buttonBg};
      color: ${theme.colors.primaryText};
      border: 1px solid ${theme.colors.buttonBorder};
      font-weight: 700;
      box-shadow: ${shadows.insetButton};
      text-transform: uppercase;
      letter-spacing: 0.2px;

      &:hover:not(:disabled) {
        background: ${theme.colors.buttonBg};
        box-shadow: ${shadows.prominent};
      }
    `,
    secondary: css`
      background: ${glass.bg};
      color: ${theme.colors.text};
      border: 1px solid rgba(255, 255, 255, 0.1);
      backdrop-filter: ${glass.backdrop};
      text-transform: uppercase;
      letter-spacing: 0.2px;

      &:hover:not(:disabled) {
        background: ${glass.bgHover};
      }
    `,
    ghost: css`
      background: transparent;
      color: ${theme.colors.textSecondary};
      border: none;
      text-transform: uppercase;
      letter-spacing: 0.2px;

      &:hover:not(:disabled) {
        color: ${theme.colors.text};
      }
    `,
    destructive: css`
      background: ${theme.colors.error};
      color: ${theme.colors.primaryText};
      border: none;
      font-weight: 700;
      box-shadow: ${shadows.insetButton};
      text-transform: uppercase;
      letter-spacing: 0.2px;

      &:hover:not(:disabled) {
        opacity: 0.9;
      }
    `,
  };

  return (
    <button
      ref={ref}
      type="button"
      className={className}
      css={[
        css`
          padding: ${s.padding};
          font-size: ${s.fontSize};
          border-radius: ${shapeRadius[shape]};
          cursor: pointer;
          transition:
            background 0.2s ease-in-out,
            color 0.2s ease-in-out,
            box-shadow 0.2s ease-in-out;
          line-height: 1.4;

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `,
        variantStyles[variant],
      ]}
      {...props}
    />
  );
});
